/**
 * Shared plumbing for the GM window family (🛡️ category, 2026-07-06).
 *
 * Every GM window is UI over the PROVEN slash-command surface
 * (ChatCommandHandler) — the window sends the exact wire the chat box would
 * (`room.send('chatMessage', {message: '/…'})`), so per-command GM gating
 * (COMMAND_PERMISSIONS) is enforced server-side for free. Client-side we only
 * read the self gmLevel to grey out buttons the server would reject anyway.
 */
import React, { useEffect, useState } from 'react';
import { GM_LEVEL_NAMES, type GMLevel, canExecuteCommand } from '@/shared/constants/GMPermissions';

export const getRoom = (): any => (globalThis as any).nm?.getRoom?.();

/** Send a slash command through the normal chat wire (server validates GM level). */
export function sendSlashCommand(cmd: string): void {
  getRoom()?.send('chatMessage', { message: cmd });
}

/** 1s re-render pulse — the room schema isn't React state (GmRaceLobbyHud pattern). */
export function useRoomPulse(ms = 1000): void {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

export function useSelfGmLevel(): GMLevel {
  const room = getRoom();
  const self = room?.state?.players?.get?.(room?.sessionId);
  return (self?.gmLevel ?? 0) as GMLevel;
}

/** True when the self player's gmLevel clears `command`'s registry tier. */
export function selfCanRun(command: string, gmLevel: GMLevel): boolean {
  return canExecuteCommand(gmLevel, command);
}

export const GmLevelChip: React.FC<{ gmLevel: GMLevel }> = ({ gmLevel }) => (
  <span
    style={{
      fontSize: 10,
      fontFamily: 'monospace',
      padding: '2px 8px',
      borderRadius: 4,
      border: `1px solid ${gmLevel >= 3 ? 'rgba(46, 204, 113, 0.5)' : 'rgba(230, 126, 34, 0.5)'}`,
      color: gmLevel >= 3 ? '#2ecc71' : '#e67e22',
    }}
    title="Your GM permission tier — buttons the server would reject are greyed out"
  >
    {GM_LEVEL_NAMES[gmLevel]} · lvl {gmLevel}
  </span>
);

export const gmBtnStyle = (enabled: boolean): React.CSSProperties => ({
  padding: '5px 10px',
  fontSize: 11,
  borderRadius: 5,
  border: '1px solid rgba(236, 240, 241, 0.25)',
  background: enabled ? 'rgba(52, 152, 219, 0.18)' : 'rgba(127, 140, 141, 0.12)',
  color: enabled ? '#ecf0f1' : '#7f8c8d',
  cursor: enabled ? 'pointer' : 'not-allowed',
});

export const gmSectionTitleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  opacity: 0.6,
  margin: '10px 0 6px',
};
